// src/lib/planModel.js — OHC Group Plan Builder shared model (ONE source of truth for /plan-check and /admin/plan-builder)
// Ported 1:1 from the OHC Group Plan Builder Page (Oct 30, 2026). Pure functions, no React, no network.
export const MODEL_VERSION = "2026-10-30.1";

export const SAMPLE = {
  enrolled: "35", fte: "42", state: "TX", industry: "prof", age: "41",
  mixEE: "55", mixES: "15", mixEC: "12", mixFam: "18",
  pepm: "650", renewal: "9", mode: "percent", pctEE: "80", pctDep: "50", flat: "500",
  risk: "medium", health: "average", design: "ppo", year: "2027", wage: "38000", hybridShare: "40",
  pCost: true, pRecruit: true, pSimple: false, pChoice: false,
};

export const INDUSTRIES = [
  { value: "prof", label: "Professional services", hourly: false, recruit: true, claimsAdj: 0 },
  { value: "tech", label: "Technology", hourly: false, recruit: true, claimsAdj: 0 },
  { value: "health", label: "Healthcare", hourly: false, recruit: true, claimsAdj: 0 },
  { value: "energy", label: "Energy / oil and gas", hourly: false, recruit: true, claimsAdj: 0.03 },
  { value: "construction", label: "Construction / trades", hourly: true, recruit: false, claimsAdj: 0.03 },
  { value: "manufacturing", label: "Manufacturing", hourly: true, recruit: false, claimsAdj: 0.02 },
  { value: "retail", label: "Retail / hospitality", hourly: true, recruit: false, claimsAdj: 0 },
  { value: "nonprofit", label: "Nonprofit", hourly: false, recruit: false, claimsAdj: 0 },
  { value: "other", label: "Other", hourly: false, recruit: false, claimsAdj: 0 },
];

export const ARRS = [
  { id: "aca", name: "ACA small-group fully insured", short: "ACA small", kind: "Traditional",
    rule: "Texas small employers with 2–50 employees (most states 1–50). Guaranteed issue, age-rated, no health underwriting.",
    risk: 1, admin: 1, recruit: 4, choice: 3, riskLabel: "Low", adminLabel: "Low",
    behavior: "Fixed premium; renews at the carrier's filed rate change (2026 median proposed small-group increase was 11%).",
    pros: ["Fixed, budgetable premium with no claims risk", "Guaranteed issue: health status cannot raise rates", "Full essential health benefits and familiar carrier plans"],
    cons: ["Pays state premium tax, carrier margin and risk load", "Community-rated pool: healthy groups subsidize others", "No refund and no claims data to manage cost"],
    why: "simple, predictable and with no health underwriting", selfFunded: false, hourlyFit: false },
  { id: "lg", name: "Large-group fully insured", short: "Large FI", kind: "Traditional",
    rule: "51+ employees in Texas. Experience-rated once the group has credible claims.",
    risk: 1, admin: 1, recruit: 4, choice: 3, riskLabel: "Low", adminLabel: "Low",
    behavior: "Fixed premium; renewal driven by the group's own claims plus trend, with pooling above a set point.",
    pros: ["Fixed premium; the carrier carries claims risk", "Experience rating rewards a good claims history", "More plan design flexibility than small group"],
    cons: ["Carrier retention and premium tax built into price", "Limited claims transparency at smaller sizes", "A bad claims year can drive a large renewal"],
    why: "fixed cost with credit for good experience", selfFunded: false, hourlyFit: false },
  { id: "level", name: "Level-funded", short: "Level", kind: "Alternative",
    rule: "Typically 5+ enrolled (some carriers 2+), medically underwritten; common up to about 200 enrolled.",
    risk: 2, admin: 1.5, recruit: 4, choice: 2.5, riskLabel: "Low–medium", adminLabel: "Low",
    behavior: "Fixed monthly payment (admin + stop-loss + claims fund); 50–67% of an unused claims fund is typically refunded or credited.",
    pros: ["Can cost less than fully insured for healthier groups (estimate, not guaranteed)", "Surplus refund when claims run low", "Monthly claims reporting builds data for future options"],
    cons: ["Medical underwriting: high-claims groups may be declined or rated up", "Renewal can jump after a bad year; run-out terms vary", "Plan sponsor duties (PCORI filing, plan document, SPD)"],
    why: "a fixed monthly cost with upside if claims run low", selfFunded: true, hourlyFit: false },
  { id: "self", name: "Self-funded with stop-loss (specific + aggregate)", short: "Self-funded", kind: "Alternative",
    rule: "Typically 100+ enrolled; 50–99 possible with strong claims history and cash reserves.",
    risk: 3, admin: 3, recruit: 4.5, choice: 3, riskLabel: "High", adminLabel: "High",
    behavior: "Fixed costs (TPA, PBM, stop-loss) plus actual claims. Specific stop-loss caps each claimant; aggregate caps total claims, commonly at 125% of expected.",
    pros: ["Avoids state premium tax and carrier margin; keeps claims surplus", "Full ERISA design freedom, no state mandates", "Full claims data for targeted cost management"],
    cons: ["Claims volatility: cost can run up to the aggregate attachment", "Cash-flow and fiduciary responsibility", "Higher admin: TPA, PBM, stop-loss and compliance filings"],
    why: "maximum control and long-run cost management", selfFunded: true, hourlyFit: false },
  { id: "rbp", name: "Reference-based pricing (self-funded)", short: "RBP", kind: "Alternative",
    rule: "Self-funded or level-funded chassis; typically 100+ enrolled, some programs from about 25.",
    risk: 3, admin: 3, recruit: 2.5, choice: 2, riskLabel: "High", adminLabel: "High",
    behavior: "Claims paid at a multiple of Medicare instead of network discounts; published reductions of 15–40% vs PPO pricing, before balance-billing support costs.",
    pros: ["Often the largest claims-cost lever; results vary", "Transparent, Medicare-anchored pricing", "Pairs well with direct contracts and DPC"],
    cons: ["Balance billing and provider pushback; member friction", "Requires strong member advocacy and education", "Recruiting perception risk vs a known PPO card"],
    why: "the deepest claims-cost lever for a cost-focused employer", selfFunded: true, hourlyFit: false },
  { id: "captive", name: "Group captive (stop-loss captive)", short: "Captive", kind: "Alternative",
    rule: "Self-funded employers pooling stop-loss risk; typically 50+ enrolled, some programs from about 25.",
    risk: 2, admin: 2.5, recruit: 4, choice: 3, riskLabel: "Medium", adminLabel: "Medium–high",
    behavior: "Self-funded claims plus a pooled captive layer; underwriting surplus can return as dividends. Reported year-one cost reductions of about 5%, more over time.",
    pros: ["Self-funding economics with pooled volatility protection", "Potential dividend from captive surplus", "Stable multi-year cost strategy"],
    cons: ["Collateral and a multi-year commitment", "Shared losses across members", "More admin and data reporting than insured"],
    why: "self-funding economics with pooled protection", selfFunded: true, hourlyFit: false },
  { id: "peo", name: "PEO master health plan", short: "PEO", kind: "Alternative",
    rule: "Any size, usually about 5–150 employees; requires co-employment for payroll and HR.",
    risk: 1, admin: 1, recruit: 4, choice: 4, riskLabel: "Low", adminLabel: "Low",
    behavior: "Pooled large-group rates under the PEO's master plan; the PEO service fee (percent of payroll or PEPM) is separate.",
    pros: ["Large-group style rates and plan menus for small employers", "PEO runs benefits admin, COBRA and ACA reporting", "Bundles HR, payroll and workers' comp"],
    cons: ["Co-employment and a PEO fee; harder to leave", "Rates can still jump at the PEO's annual renewal", "Less control over carrier and plan design"],
    why: "big-company benefits with low admin", selfFunded: false, hourlyFit: false },
  { id: "ichra", name: "Individual coverage HRA (ICHRA)", short: "ICHRA", kind: "Alternative",
    rule: "Any size. Employer sets a tax-free allowance by class; employees buy individual coverage. A class cannot be offered both ICHRA and the group plan.",
    risk: 1, admin: 1.5, recruit: 3, choice: 5, riskLabel: "Low (employer)", adminLabel: "Low",
    behavior: "Employer cost fixed at the allowance it sets; employees absorb individual-market premium changes.",
    pros: ["Fully predictable employer budget", "Maximum employee plan choice", "Satisfies the employer mandate if affordable"],
    cons: ["Texas benchmark silver premiums rose about 35% for 2026", "Narrower individual-market provider choices", "Employees offered an affordable ICHRA lose premium tax credits"],
    why: "a fixed employer budget and full choice", selfFunded: false, hourlyFit: true },
  { id: "qsehra", name: "QSEHRA", short: "QSEHRA", kind: "Alternative",
    rule: "Under 50 FTEs and no group health plan offered; 2026 caps $6,450 self-only and $13,100 family.",
    risk: 1, admin: 1, recruit: 2.5, choice: 5, riskLabel: "Low (employer)", adminLabel: "Low",
    behavior: "Fixed allowance up to IRS caps; reduces employees' premium tax credits dollar for dollar.",
    pros: ["Simplest tax-free reimbursement for small employers", "No minimum participation or contribution rules", "Employee picks any individual plan"],
    cons: ["Capped allowance limits richness", "Not available alongside any group plan", "Weak recruiting signal vs a group plan"],
    why: "the lowest-admin defined contribution", selfFunded: false, hourlyFit: true },
  { id: "hybrid", name: "ICHRA + group plan by class", short: "Hybrid", kind: "Alternative",
    rule: "Group plan for core classes, ICHRA for others (part-time, seasonal, other states). Minimum class sizes apply when both are offered: 10 if under 100 employees.",
    risk: 1.5, admin: 2.5, recruit: 3.5, choice: 4, riskLabel: "Low", adminLabel: "Medium–high",
    behavior: "Group premium for the core class plus a fixed ICHRA allowance for other classes.",
    pros: ["Keeps a group plan for core staff while capping cost for others", "Fits multi-state and part-time populations", "Removes high-turnover classes from group rating"],
    cons: ["Class rules and minimum class sizes add complexity", "Two enrollment experiences to support", "Can affect carrier participation requirements"],
    why: "targeted cost control without dropping the group plan", selfFunded: false, hourlyFit: true },
  { id: "mec", name: "MEC / MVP + indemnity", short: "MEC/MVP", kind: "Alternative",
    rule: "Mostly ALEs with hourly, high-turnover staff. MEC avoids the 4980H(a) penalty; an affordable MVP (60% value) also avoids 4980H(b).",
    risk: 1, admin: 2, recruit: 1.5, choice: 2, riskLabel: "Low (employer)", adminLabel: "Medium",
    behavior: "Low premium; thin benefits shift costs to employees; voluntary indemnity fills gaps.",
    pros: ["Lowest-premium path to employer-mandate compliance", "Affordable for low-wage workforces", "Indemnity riders add hospital and accident cash benefits"],
    cons: ["Thin coverage: high employee out-of-pocket exposure", "Weak for recruiting professional staff", "MVP designs draw scrutiny: must truly meet 60% minimum value"],
    why: "mandate compliance at the lowest premium", selfFunded: false, hourlyFit: true },
  { id: "dpc", name: "Direct primary care + HSA HDHP", short: "DPC+HDHP", kind: "Alternative",
    rule: "Any size; pairs DPC membership with an HSA-qualified HDHP. HSA-compatible from 2026 if fees are at most $150/mo individual or $300 family.",
    risk: 1, admin: 2, recruit: 4, choice: 3, riskLabel: "Low", adminLabel: "Medium",
    behavior: "HDHP premium plus a fixed DPC membership fee; downstream claims effects are not modeled.",
    pros: ["Unlimited primary care with no copays", "HSA eligibility preserved under the 2026 rules", "Can reduce ER, urgent care and specialist use"],
    cons: ["Adds a per-member fee on top of premium", "Value depends on local DPC capacity and engagement", "Claims effects show up mainly when level- or self-funded"],
    why: "strong primary care access that complements an HSA", selfFunded: false, hourlyFit: false },
];

export const RATIOS = [1, 2.0, 1.8, 2.9];
export const DESIGN_F = { ppo: 1, hdhp: 0.88, hmo: 0.92 };
export const TX_BENCH = 661;
const CHILD_F = 0.765 / 1.278;
export const QS_EE = 6450;
export const QS_FAM = 13100;
const RBP_SAVE = 0.2;
const MEC_F = 0.55;
const DPC_FEES = [65, 130, 65 + 1.5 * 35, 130 + 2 * 35];
const CURVE = [[21, 1.0], [25, 1.004], [30, 1.135], [35, 1.222], [40, 1.278], [45, 1.441], [50, 1.786], [55, 2.231], [60, 2.714], [64, 3.0]];

export function num(s, d) { const v = parseFloat(s); return isFinite(v) ? v : d; }
export function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }
export function money(v) { return (v < 0 ? "-$" : "$") + Math.round(Math.abs(v)).toLocaleString("en-US"); }
export function vsWord(s) { return s >= 0 ? `${(s * 100).toFixed(1)}% below` : `${(-s * 100).toFixed(1)}% above`; }
export function signed(s) { return (s <= 0 ? "+" : "−") + (Math.abs(s) * 100).toFixed(1) + "%"; }
export function capFirst(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
export function ageFactor(age) {
  const a = clamp(age, 21, 64);
  for (let k = 1; k < CURVE.length; k++) {
    const [x1, y1] = CURVE[k - 1];
    const [x2, y2] = CURVE[k];
    if (a <= x2) return (y1 + ((y2 - y1) * (a - x1)) / (x2 - x1)) / 1.278;
  }
  return 3.0 / 1.278;
}

export function model(i) {
  const n = Math.max(1, Math.round(num(i.enrolled, 35)));
  const fte = Math.max(1, num(i.fte, n));
  const mixRaw = [i.mixEE, i.mixES, i.mixEC, i.mixFam].map((s) => Math.max(0, num(s, 0)));
  const mixSum = mixRaw.reduce((a, b) => a + b, 0) || 1;
  const mix = mixRaw.map((v) => v / mixSum);
  const counts = mix.map((m) => m * n);
  const blend = mix.reduce((a, m, k) => a + m * RATIOS[k], 0) || 1;
  const pepm = Math.max(0, num(i.pepm, 650));
  const ren = num(i.renewal, 9) / 100;
  const eeRate = pepm / blend;
  const curRates = RATIOS.map((r) => eeRate * r);
  const renRates = curRates.map((r) => r * (1 + ren));
  const pEE = clamp(num(i.pctEE, 80), 0, 100) / 100;
  const pDep = clamp(num(i.pctDep, 50), 0, 100) / 100;
  const flat = Math.max(0, num(i.flat, 500));
  const erShare = (rates) => rates.map((r) => (i.mode === "percent" ? pEE * rates[0] + pDep * (r - rates[0]) : Math.min(flat, r)));
  const split = (rates, er) => {
    const e = er ?? erShare(rates);
    let total = 0, employer = 0;
    rates.forEach((r, k) => { total += r * counts[k] * 12; employer += Math.min(e[k], r) * counts[k] * 12; });
    return { total, employer, employee: total - employer, worst: total };
  };
  const groupAt = (F) => split(renRates.map((r) => r * F));
  const baseline = groupAt(1);
  const D = DESIGN_F[i.design] ?? 1;
  const h = i.health === "healthy" ? 0 : i.health === "average" ? 1 : 2;
  const ind = INDUSTRIES.find((x) => x.value === i.industry) ?? INDUSTRIES[0];
  const small = i.state === "TX" ? fte >= 2 && fte <= 50 : fte <= 50;
  const ale = fte >= 50;

  const age = clamp(num(i.age, 41), 21, 64);
  const af = ageFactor(age);
  const P = TX_BENCH * (1 + ren);
  const indiv = [P * af, 2 * P * af, P * af + 1.5 * CHILD_F * P, 2 * P * af + 2 * CHILD_F * P];
  const allowCur = erShare(curRates).map((v, k) => Math.min(v, curRates[k]));
  const ichra = split(indiv, allowCur);
  const qAllow = allowCur.map((v, k) => Math.min(v, k === 0 ? QS_EE / 12 : QS_FAM / 12));
  const qsehra = split(indiv, qAllow);

  const f = n < 100 ? 0.26 : 0.22;
  const c = [0.64, 0.72, 0.84][h] * (1 + ind.claimsAdj);
  const fiF = small ? 1 : [0.97, 1, 1.05][h];
  const B = baseline.total;

  const lvM = groupAt([0.88, 0.94, 1.04][h] * (1 + ind.claimsAdj) * D);
  const refund = [0.03, 0.01, 0][h] * D * B;
  const s = clamp(num(i.hybridShare, 40), 0, 100) / 100;
  const core = groupAt(fiF * D);
  const dpcG = groupAt(fiF * DESIGN_F.hdhp);
  const feeTot = DPC_FEES.reduce((a, fee, k) => a + fee * counts[k] * 12, 0);
  const hyb = {
    total: (1 - s) * core.total + s * ichra.total,
    employer: (1 - s) * core.employer + s * ichra.employer,
    employee: (1 - s) * core.employee + s * ichra.employee,
  };

  const costs = {
    aca: groupAt(D),
    lg: groupAt([0.97, 1, 1.05][h] * D),
    level: { total: lvM.total - refund, employer: lvM.employer - refund, employee: lvM.employee, worst: lvM.total },
    self: { ...groupAt((f + c) * D), worst: (f + 1.25 * c) * D * B },
    rbp: { ...groupAt((f + 0.03 + c * (1 - RBP_SAVE)) * D), worst: (f + 0.03 + 1.25 * c * (1 - RBP_SAVE)) * D * B },
    captive: { ...groupAt((0.24 + c - [0.02, 0.01, 0][h]) * D), worst: (0.24 + 1.25 * c) * D * B },
    peo: groupAt([0.98, 1, 1.03][h] * D),
    ichra,
    qsehra,
    hybrid: { ...hyb, worst: hyb.total },
    mec: groupAt(MEC_F),
    dpc: { total: dpcG.total + feeTot, employer: dpcG.employer + feeTot, employee: dpcG.employee, worst: dpcG.total + feeTot },
  };

  const elig = (id) => {
    switch (id) {
      case "aca": return small ? ["Eligible", "Within the small-group size band"] : ["Not eligible", "Over 50 employees: large-group market"];
      case "lg": return fte > 50 ? ["Eligible", "51+ employees"] : ["Not eligible", "50 or fewer employees: small-group market"];
      case "level":
        if (n < 2) return ["Not eligible", "Needs at least 2 enrolled"];
        if (n < 5) return ["Stretch", "Under 5 enrolled: few carriers will quote"];
        if (h === 2) return ["Stretch", "High-claims groups may be declined or rated up at underwriting"];
        return n <= 250 ? ["Eligible", "Within the typical level-funded size range"] : ["Stretch", "Larger groups usually move to full self-funding"];
      case "self": return n >= 100 ? ["Eligible", "100+ enrolled"] : n >= 50 ? ["Stretch", "50–99 enrolled: volatile, needs reserves"] : ["Not eligible", "Too small to absorb claims volatility"];
      case "rbp": return n >= 100 ? ["Eligible", "100+ enrolled"] : n >= 25 ? ["Stretch", "Under 100: only via level-funded RBP programs"] : ["Not eligible", "Too small for most RBP programs"];
      case "captive": return n >= 50 ? ["Eligible", "50+ enrolled"] : n >= 25 ? ["Stretch", "25–49 enrolled: limited captive programs"] : ["Not eligible", "Below typical captive minimums"];
      case "peo": return n <= 500 ? ["Eligible", "Requires co-employment through the PEO"] : ["Stretch", "Large groups usually outgrow PEO plans"];
      case "ichra": return ["Eligible", "Any size; class rules apply"];
      case "qsehra": return fte < 50 ? ["Eligible", "Under 50 FTEs; must replace the group plan"] : ["Not eligible", "50+ FTEs (ALE)"];
      case "hybrid": return n * s >= 10 ? ["Eligible", `About ${Math.round(n * s)} employees in the ICHRA class`] : ["Stretch", `About ${Math.round(n * s)} in the ICHRA class: under the 10-person minimum for most class types`];
      case "mec": return ale ? ["Eligible", "ALE: built for employer-mandate compliance"] : ["Stretch", "Under 50 FTEs: no mandate driver"];
      case "dpc": return ["Eligible", "Any size"];
      default: return ["Not eligible", "Unknown arrangement"];
    }
  };

  const tol = i.risk === "low" ? 1 : i.risk === "medium" ? 2 : 3;
  const w = { cost: 0.3, risk: 0.25, admin: 0.15, recruit: 0.15, choice: 0.15 };
  if (i.pCost) w.cost += 0.2;
  if (i.pRecruit) w.recruit += 0.2;
  if (i.pSimple) w.admin += 0.2;
  if (i.pChoice) w.choice += 0.2;
  if (ind.recruit) w.recruit += 0.05;
  const wSum = w.cost + w.risk + w.admin + w.recruit + w.choice;

  const results = ARRS.map((a) => {
    const cost = costs[a.id];
    const [st, note] = elig(a.id);
    const savTot = (B - cost.total) / B;
    const savEr = baseline.employer > 0 ? (baseline.employer - cost.employer) / baseline.employer : 0;
    const sCost = clamp(50 + 250 * (0.5 * savTot + 0.5 * savEr), 0, 100);
    const sRisk = clamp(100 - Math.max(0, a.risk - tol) * 45 - (h === 2 && a.selfFunded ? 15 : 0), 0, 100);
    const sAdmin = clamp(100 - (a.admin - 1) * 40, 0, 100);
    const sRecruit = a.recruit * 20;
    const sChoice = a.choice * 20;
    let fit = (w.cost * sCost + w.risk * sRisk + w.admin * sAdmin + w.recruit * sRecruit + w.choice * sChoice) / wSum;
    if (ind.hourly && a.hourlyFit) fit += 6;
    if (ind.hourly && a.selfFunded) fit -= 4;
    if (st === "Stretch") fit *= 0.85;
    if (st === "Not eligible") fit = 0;
    return { a, elig: st, note, cost, factor: cost.total / B, savTot, savEr, fit: clamp(fit, 0, 100), sCost, sRisk, sAdmin, sRecruit, sChoice };
  });

  const affPct = i.year === "2027" ? 0.1022 : 0.0996;
  const wage = Math.max(0, num(i.wage, 38000));
  const affLimit = (wage * affPct) / 12;
  const coreRates = renRates.map((r) => r * fiF * D);
  const coreEE = coreRates[0] - Math.min(erShare(coreRates)[0], coreRates[0]);
  const ichraEE = indiv[0] - allowCur[0];
  const lives = counts[0] + counts[1] * 2 + counts[2] * 2.5 + counts[3] * 4;

  return {
    n, fte, small, ale, baseline, results, tol, ind, affPct, affLimit, coreEE, ichraEE,
    pcori: lives * 3.84, lives, penaltyA: ale ? Math.max(0, fte - 30) * 3340 : 0, eeRate, indivEE: indiv[0], allowEE: allowCur[0], s,
  };
}

// Ranking used by BOTH tools: eligible + stretch only, sorted by fit desc; ties broken by ARRS order (stable sort).
export function rank(m) {
  return [...m.results].filter((r) => r.elig !== "Not eligible").sort((x, y) => y.fit - x.fit);
}
export function top3(m) { return rank(m).slice(0, 3); }

export function reason(r, risk, tol, i) {
  const parts = [`Est. total cost ${vsWord(r.savTot)} renewal, employer cost ${vsWord(r.savEr)}`];
  parts.push(r.a.risk <= tol ? `risk fits a ${risk} tolerance` : `risk is above a ${risk} tolerance`);
  if (i.pSimple && r.a.admin <= 1.5) parts.push("low admin");
  if (i.pRecruit && r.a.recruit >= 4) parts.push("competitive for recruiting");
  if (i.pChoice && r.a.choice >= 4) parts.push("broad employee choice");
  if (i.pCost && r.savTot > 0.03) parts.push("supports cost containment");
  if (r.elig === "Stretch") parts.push(`stretch: ${r.note.toLowerCase()}`);
  return `${capFirst(r.a.why)}. ${capFirst(parts.join("; "))}.`;
}

export const HSA_YEAR = {
  "2026": { ded: "$1,700 / $3,400", oop: "$8,500 / $17,000", hsa: "$4,400 / $8,750", aca: "$10,600 / $21,200" },
  "2027": { ded: "$1,750 / $3,500", oop: "$8,700 / $17,400", hsa: "$4,500 / $9,000", aca: "$12,000 / $24,000" },
};

// ---------------- Prospect pre-screen (/plan-check) ----------------
export const PC_OPTIONS = {
  coverage: [
    { value: "fully_insured", label: "Yes, a traditional group plan through a carrier" },
    { value: "level_or_self", label: "Yes, a level-funded or self-funded plan" },
    { value: "peo", label: "Yes, through a PEO" },
    { value: "hra", label: "We reimburse individual plans (ICHRA or QSEHRA)" },
    { value: "none", label: "No, we don't offer health coverage yet" },
    { value: "not_sure", label: "Not sure" },
  ],
  cost: [
    { value: "lt500", label: "Under $500", pepm: 450 },
    { value: "500_700", label: "$500 – $700", pepm: 600 },
    { value: "700_900", label: "$700 – $900", pepm: 800 },
    { value: "gt900", label: "Over $900", pepm: 1000 },
    { value: "unknown", label: "Not sure / no plan today", pepm: 650 },
  ],
  renewal: [
    { value: "lt5", label: "Under 5%", pct: 3 },
    { value: "5_9", label: "5% – 9%", pct: 7 },
    { value: "10_14", label: "10% – 14%", pct: 12 },
    { value: "15_19", label: "15% – 19%", pct: 17 },
    { value: "20p", label: "20% or more", pct: 22 },
    { value: "unknown", label: "Haven't seen it yet / not sure", pct: 9 },
  ],
  priorities: [
    { value: "cost", label: "Control cost" },
    { value: "recruit", label: "Recruit and keep good people" },
    { value: "simple", label: "Keep it simple to run" },
    { value: "choice", label: "Give employees more choice" },
  ],
  risk: [
    { value: "low", label: "A fixed, predictable bill. No surprises." },
    { value: "medium", label: "Some ups and downs are fine within set limits." },
    { value: "high", label: "Comfortable with claims swings for long-term control." },
  ],
  health: [
    { value: "healthy", label: "Mostly healthy, few claims" },
    { value: "average", label: "About typical" },
    { value: "high", label: "A few large or ongoing claims" },
    { value: "not_sure", label: "Not sure" },
  ],
  timing: [
    { value: "lt60", label: "Within 60 days" },
    { value: "2_4", label: "In 2 – 4 months" },
    { value: "4_6", label: "In 4 – 6 months" },
    { value: "6p", label: "More than 6 months out" },
    { value: "exploring", label: "Just exploring" },
  ],
};

// Answers -> full builder Inputs. Fields the screen doesn't ask use the defaults below.
export function screenToInputs(a) {
  const fte = Math.max(1, Math.round(num(a.fte, 10)));
  const enrolledRaw = num(a.enrolled, NaN);
  const enrolled = isFinite(enrolledRaw) && enrolledRaw > 0 ? Math.round(enrolledRaw) : Math.max(1, Math.round(fte * 0.8));
  const cost = PC_OPTIONS.cost.find((o) => o.value === a.cost) ?? PC_OPTIONS.cost[4];
  const ren = PC_OPTIONS.renewal.find((o) => o.value === a.renewal) ?? PC_OPTIONS.renewal[5];
  const pr = Array.isArray(a.priorities) ? a.priorities : [];
  return {
    enrolled: String(enrolled), fte: String(fte), state: a.state === "TX" ? "TX" : "OTHER",
    industry: INDUSTRIES.some((x) => x.value === a.industry) ? a.industry : "other", age: "41",
    mixEE: "55", mixES: "15", mixEC: "12", mixFam: "18",
    pepm: String(cost.pepm), renewal: String(ren.pct), mode: "percent", pctEE: "80", pctDep: "50", flat: "500",
    risk: ["low", "medium", "high"].includes(a.risk) ? a.risk : "low",
    health: a.health === "healthy" ? "healthy" : a.health === "high" ? "high" : "average",
    design: "ppo", year: "2027", wage: "38000", hybridShare: "40",
    pCost: pr.includes("cost"), pRecruit: pr.includes("recruit"), pSimple: pr.includes("simple"), pChoice: pr.includes("choice"),
  };
}

// Plain-language copy for prospects. No dollar figures, no percentages, no savings claims.
export const PROSPECT = {
  aca: { title: "Traditional small-group plan", line: "A fixed monthly premium with no health questions, so one big claim can't change your rate." },
  lg: { title: "Traditional large-group plan", line: "A fixed premium where a good claims history can earn you better renewals." },
  level: { title: "Level-funded plan", line: "A fixed monthly cost like insurance; depending on the plan's terms, part of any unused claims fund may be refunded or credited." },
  self: { title: "Self-funded plan with stop-loss protection", line: "You pay actual claims with insurance capping the big ones, for the most control over design and data." },
  rbp: { title: "Reference-based pricing", line: "A self-funded approach that pays providers set, transparent prices tied to public benchmarks instead of network discounts." },
  captive: { title: "Group captive", line: "Self-funding alongside other employers who share the large-claim risk, with possible dividends." },
  peo: { title: "PEO health plan", line: "Big-company plan options through a PEO that also handles payroll, HR and benefits paperwork." },
  ichra: { title: "ICHRA (individual coverage allowance)", line: "You set a fixed tax-free monthly allowance and each employee picks their own individual plan." },
  qsehra: { title: "QSEHRA (small-employer allowance)", line: "The simplest way for a small employer to reimburse individual plans tax-free, up to IRS limits." },
  hybrid: { title: "Group plan plus ICHRA by employee class", line: "Keep a group plan for core staff and give other groups, like part-time or out-of-state staff, an allowance." },
  mec: { title: "MEC / MVP plan", line: "A basic-coverage option some larger hourly workforces use to meet the employer mandate." },
  dpc: { title: "Direct primary care + HSA plan", line: "Unlimited primary care visits paired with an HSA-qualified plan." },
};

export const PROSPECT_ELIG = {
  aca: { Eligible: "Fits your size: Texas small groups are 2–50 employees." },
  lg: { Eligible: "Fits your size: more than 50 employees." },
  level: { Eligible: "Your size is in the usual range; carriers ask health questions to quote.", Stretch: "Possible, but carriers may limit quotes for groups like yours." },
  self: { Eligible: "Your size is in the usual range for self-funding.", Stretch: "Possible at your size with healthy claims and cash reserves." },
  rbp: { Eligible: "Your size is in the usual range.", Stretch: "At your size, usually only through level-funded programs." },
  captive: { Eligible: "Your size is in the usual range for captives.", Stretch: "A few captive programs accept groups your size." },
  peo: { Eligible: "Available at your size; requires joining a PEO for payroll and HR.", Stretch: "Possible, though larger groups often outgrow PEO plans." },
  ichra: { Eligible: "Available at any size; class rules apply." },
  qsehra: { Eligible: "Available under 50 full-time equivalents, in place of a group plan." },
  hybrid: { Eligible: "Your headcount supports separate employee classes.", Stretch: "Possible, but minimum class sizes may limit how classes are set up." },
  mec: { Eligible: "Built for employers with 50+ full-time equivalents.", Stretch: "Less common under 50 full-time equivalents." },
  dpc: { Eligible: "Available at any size, depending on local DPC practices." },
};

function tags(r, i, tol) {
  const t = [];
  if (i.pCost && r.savTot > 0.03) t.push("worth comparing on cost");
  if (i.pSimple && r.a.admin <= 1.5) t.push("simple to run");
  if (i.pRecruit && r.a.recruit >= 4) t.push("competitive for recruiting");
  if (i.pChoice && r.a.choice >= 4) t.push("gives employees broad choice");
  if (r.a.risk <= 1) t.push(i.risk === "low" ? "a predictable bill with no claims risk for you" : "no claims risk for you");
  else if (r.a.risk <= tol) t.push("fits your comfort with risk");
  return t.slice(0, 2);
}

export function screenResult(answers) {
  const inputs = screenToInputs(answers);
  const m = model(inputs);
  const tol = m.tol;
  const top = top3(m).map((r, idx) => {
    const tg = tags(r, inputs, tol);
    return {
      rank: idx + 1, id: r.a.id, title: PROSPECT[r.a.id].title, line: PROSPECT[r.a.id].line,
      why: tg.length ? `Why it fits: ${tg.join("; ")}.` : "",
      elig: r.elig, eligNote: PROSPECT_ELIG[r.a.id][r.elig] ?? "",
      fit: Math.round(r.fit), // stored for admin; NOT shown to prospects
      riskLabel: r.a.riskLabel, adminLabel: r.a.adminLabel,
    };
  });
  const notes = [];
  const underwritten = top.some((t) => ["level", "self", "rbp", "captive"].includes(t.id));
  if (answers.timing === "lt60" && underwritten) notes.push("Some of these options need health questions or claims data and 30–60+ days of lead time. With your timing, we'd start right away.");
  if (answers.coverage === "none" || answers.cost === "unknown") notes.push("You didn't share a current cost, so this screen used a typical Texas small-group cost as a placeholder.");
  if (answers.coverage === "peo") notes.push("Since you're in a PEO today, we'd compare your PEO's renewal against these options side by side.");
  if (inputs.state !== "TX") notes.push("This screen uses Texas market rules. Rules in your state may differ.");
  if (m.fte < 2) notes.push("Texas group plans generally need at least 2 employees; allowance options like ICHRA or QSEHRA may fit better.");
  return {
    modelVersion: MODEL_VERSION, inputs, top, notes,
    summary: { fte: m.fte, enrolled: m.n, small: m.small, ale: m.ale },
    allFit: Object.fromEntries(m.results.map((r) => [r.a.id, { fit: Math.round(r.fit), elig: r.elig }])),
  };
}
